// src/contexts/AttendanceContext.jsx
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { get } from 'api';
import { post } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { getLocation } from 'services/getLocation';
import { useAuth } from './AuthProvider';

const AttendanceContext = createContext();

export const useAttendance = () => useContext(AttendanceContext);

export function AttendanceProvider({ children }) {
  const { sessionUser } = useAuth;
  const [todayRecord, setTodayRecord] = useState(null); // attendance doc or null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isClockedIn = !!(todayRecord && todayRecord.loginTime && !todayRecord.logoutTime);
  const [history, setHistory] = useState([]);
  const [month, setMonth] = useState(moment().format('YYYY-MM'));
  // const[loading, setLoading] = useState(false);
  const fetchHistory = async () => {
    const response = await post('users/getHistory', { month: month });
    if (response.status == 200) {
      setHistory(response.data);
    }
  };
  useEffect(() => {
    fetchHistory();
  }, [month]);
  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const res = await get('users/getToday');
      setTodayRecord(res.data); // data is raw doc or null
      setError(null);
    } catch (err) {
      console.error('attendance.today error', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // optionally poll every x seconds for live update in header
    const t = setInterval(refresh, 30_000); // every 30s
    return () => clearInterval(t);
  }, [refresh, sessionUser]);

  const doPunchIn = useCallback(async () => {
    setLoading(true);
    const coordinates = await getLocation(); // Try to get location

    if (!coordinates || coordinates.length !== 2) {
      enqueueSnackbar('Unable to get your location. Please enable location access.', {
        variant: 'error',
      });
      setLoading(false);
      return;
    }

    const res = await post('users/punchIn', { coordinates, source: 'web' });
    if (res.status == 200) {
      fetchHistory();
      refresh();
      setTodayRecord(res.data);
      setLoading(false);
      return res.data;
    } else {
      enqueueSnackbar(res.message || 'Error while punchIn', {
        variant: 'error',
      });
      setLoading(false);
    }
  }, []);

  const doPunchOut = useCallback(async () => {
    const coordinates = await getLocation(); // Try to get location

    if (!coordinates || coordinates.length !== 2) {
      enqueueSnackbar('Unable to get your location. Please enable location access.', {
        variant: 'error',
      });
      return;
    }

    const res = await post('users/punchOut', { coordinates, source: 'web' });
    if (res.status == 200) {
      setTodayRecord(res.data);
      fetchHistory();
      refresh();
      return res.data;
    } else {
      enqueueSnackbar(res.message || 'Error while punchIn', {
        variant: 'error',
      });
    }
  }, []);

  const value = {
    history,
    activeShift: todayRecord,
    loading,
    error,
    isClockedIn,
    refresh,
    punchIn: doPunchIn,
    punchOut: doPunchOut,
    month,
    setMonth,
    fetchHistory,
  };

  return <AttendanceContext.Provider value={value}>{children}</AttendanceContext.Provider>;
}
